import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { io, Socket } from "socket.io-client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useCelebration } from "@/context/CelebrationContext";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { getNativeTokens } from "@/integrations/clients/carAuction/nativeAuth";
import { playOutbidFeedback } from "@/lib/alerts/alertFeedback";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import { resolveNotificationNavigationPath } from "@/lib/navigation/notificationNavigation";

const SERVER_URL =
  import.meta.env.VITE_API_BASE_URL_SOCKET_IO || "http://localhost:5001";

type PersonalEvent = {
  type: string;
  title?: string;
  body?: string;
  listingId?: string;
  listingTitle?: string;
  regNumber?: string;
  campaignId?: string;
  listingUrl?: string;
  route?: string;
};

function vehicleLabel(evt: PersonalEvent): string | undefined {
  const title = typeof evt.listingTitle === "string" ? evt.listingTitle.trim() : "";
  const reg = typeof evt.regNumber === "string" ? evt.regNumber.trim() : "";
  if (title && reg && !title.includes(reg)) return `${title} (${reg})`;
  return title || reg || undefined;
}

/**
 * Always-on authenticated socket room for personal alerts (outbid, win)
 * even when the user is not on a listing detail page.
 */
export function usePersonalRealtime() {
  const { user, isLoading } = useAuth();
  const { isNative } = useNativePlatform();
  const { celebrate } = useCelebration();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = dashboardLabels(language)?.realtimeAlerts;
  const socketRef = useRef<Socket | null>(null);
  const celebrateRef = useRef(celebrate);
  const navigateRef = useRef(navigate);
  const queryClientRef = useRef(queryClient);
  celebrateRef.current = celebrate;
  navigateRef.current = navigate;
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (isLoading || !user) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    const options: {
      path: string;
      transports: string[];
      withCredentials: boolean;
      auth?: { token: string };
    } = {
      path: "/socket.io",
      transports: ["websocket"],
      withCredentials: true,
    };

    if (isNative) {
      const accessToken = getNativeTokens()?.accessToken;
      if (accessToken) options.auth = { token: accessToken };
    }

    const socket = io(SERVER_URL, options);
    socketRef.current = socket;

    const joinPersonal = () => {
      socket.emit("joinPersonal");
    };
    if (socket.connected) joinPersonal();
    socket.on("connect", joinPersonal);

    const onPersonal = (evt: PersonalEvent) => {
      const type = String(evt?.type || "");
      if (type === "BID_OUTBID") {
        playOutbidFeedback();
        const auctionPath = resolveNotificationNavigationPath(
          evt.listingUrl || evt.route || "",
          evt as unknown as Record<string, unknown>,
        );
        toast.warning(
          evt.title || labels?.outbidTitle || "Du har blivit överbjuden",
          {
            id: "bilenia-outbid",
            description:
              evt.body ||
              labels?.outbidBody ||
              "Någon har lagt ett högre bud.",
            duration: 12000,
            action: auctionPath
              ? {
                  label: labels?.outbidAction ?? "Till auktionen",
                  onClick: () => navigateRef.current(auctionPath),
                }
              : undefined,
          },
        );
        return;
      }
      if (type === "AUCTION_WON" || type === "PURCHASE_CONFIRMED") {
        celebrateRef.current({
          kind: type === "AUCTION_WON" ? "auction_won" : "purchase",
          title: evt.title,
          listingTitle: vehicleLabel(evt),
          subtitle: evt.body,
        });
        // Confetti is instant; listing detail cache must refetch or BiddingInfo
        // stays on "Slutför…" / Aktiv until a hard refresh.
        void queryClientRef.current.invalidateQueries({ queryKey: ["listing"] });
        void queryClientRef.current.invalidateQueries({ queryKey: ["listings"] });
      }
    };

    socket.on("personalNotification", onPersonal);

    return () => {
      socket.off("connect", joinPersonal);
      socket.off("personalNotification", onPersonal);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [
    user,
    isLoading,
    isNative,
    labels?.outbidTitle,
    labels?.outbidBody,
    labels?.outbidAction,
  ]);
}
